import { type JSX, type RefObject } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';

/**
 * One titled block of the explainer. Kept local so the prose below reads as prose — the whole point of
 * this module is that the copy can be edited without touching the switcher's menu logic.
 */
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-1.5">
      <h3 className="font-heading text-sm font-semibold text-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Bullet list shared by the three list sections. */
function Bullets({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
      {children}
    </ul>
  );
}

/**
 * "What is a workspace?" — the explainer behind the switcher's *What is it?* link. Content only: the
 * caller owns the open state, and `DialogContent` already caps its height and scrolls its own body, so
 * this long text never stretches the page.
 *
 * `returnFocusRef` is where focus goes when the dialog closes, and it is not optional polish. Radix
 * dialog 1.1.15 restores focus to `DialogTrigger`'s ref, which a state-controlled dialog like this one
 * does not have; `FocusScope` then falls back to whatever was focused before, and when the dialog was
 * opened from a menu item that element is already unmounted — so focus lands on `<body>`. Handling
 * `onCloseAutoFocus` ourselves (preventDefault, then focus the anchor) is what keeps the keyboard user
 * where they were. The `preventDefault` also short-circuits Radix's own restore, which composes after
 * ours and would otherwise be a no-op anyway.
 */
export function WorkspaceAboutDialog({
  open,
  onOpenChange,
  returnFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onCloseAutoFocus={(event) => {
          const anchor = returnFocusRef?.current;
          if (!anchor) return;
          event.preventDefault();
          anchor.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>What is a workspace?</DialogTitle>
          <DialogDescription>
            A workspace is where your work with CTI lives — chats, knowledge,
            files, connectors and the agent&apos;s instructions. Switch
            workspaces and all of it switches with you: you always work in one
            workspace at a time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Section title="Your personal workspace">
            <p className="text-sm text-muted-foreground">
              Every account gets one, marked <em>Personal</em>. It is yours
              alone and nobody can be added to it. Use it for everyday work and
              for anything you would rather keep to yourself.
            </p>
          </Section>

          <Section title="Shared workspaces">
            <p className="text-sm text-muted-foreground">
              Create one for a team, a project or a client, then add people to
              it. Everyone works against the same knowledge, the same connectors
              and the same agent setup, so the answers people get are consistent
              no matter who asks.
            </p>
          </Section>

          <Section title="Who can do what">
            <Bullets>
              <li>
                <strong className="font-medium text-foreground">Owner</strong> —
                everything, including managing members and deleting the
                workspace.
              </li>
              <li>
                <strong className="font-medium text-foreground">Editor</strong>{' '}
                — the day-to-day setup: knowledge, connectors, credentials,
                agent instructions; can invite editors and viewers.
              </li>
              <li>
                <strong className="font-medium text-foreground">Viewer</strong>{' '}
                — reads and chats, changes no settings.
              </li>
            </Bullets>
          </Section>

          <Section title="What lives in a workspace">
            <Bullets>
              <li>
                <strong className="font-medium text-foreground">Chats</strong> —
                your chats stay yours. You also see chats shared with the
                workspace and chats you were added to.
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Knowledge
                </strong>{' '}
                — SharePoint folders and files the agent searches when it
                answers you. Everything here is readable by every member,
                viewers included, so add only what the whole workspace may see.
                Changed something in SharePoint? Press{' '}
                <strong className="font-medium text-foreground">Reindex</strong>{' '}
                on the source to pull the change in.
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Artifacts
                </strong>{' '}
                — files created in chats or uploaded along the way. Visible to
                the people in the chat they came from, or only to you if you
                uploaded them on the Artifacts page, until you share them with
                the workspace.
              </li>
              <li>
                <strong className="font-medium text-foreground">
                  Connectors
                </strong>{' '}
                and{' '}
                <strong className="font-medium text-foreground">
                  Credentials
                </strong>{' '}
                — connect Jira, GitHub, SharePoint and the rest once; the whole
                workspace uses that connection.
              </li>
              <li>
                <strong className="font-medium text-foreground">Agent</strong> —
                instructions that apply to every chat in this workspace.
              </li>
              <li>
                <strong className="font-medium text-foreground">Quality</strong>
                ,{' '}
                <strong className="font-medium text-foreground">
                  Developer
                </strong>
                ,{' '}
                <strong className="font-medium text-foreground">
                  Dashboards
                </strong>
                , <strong className="font-medium text-foreground">Decks</strong>{' '}
                — scoped to the workspace as well, so each one shows this
                workspace&apos;s data and nothing else.
              </li>
            </Bullets>
          </Section>

          <Section title="Good to know">
            <Bullets>
              <li>
                The agent only ever sees the workspace you are in — its
                knowledge, its connectors, its instructions. It cannot reach
                another workspace&apos;s data.
              </li>
              <li>
                Nothing moves when you switch: content belongs to the workspace
                it was created in.
              </li>
              <li>
                Deleting a workspace deletes its content with it, knowledge and
                files included.
              </li>
            </Bullets>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
